import type { ChatMessage } from '../types/chat'
import { getSupportedLanguage } from './chatUiLocale'
import { findPendingPatientOptionsMessage } from './patientOptionOrdinal'

/** Text sent to `/chat/command` for global intent cancel (see backend `_INTENT_CANCEL_UTTERANCE`). */
export function globalIntentCancelCommandText(): string {
  return getSupportedLanguage() === 'fr' ? 'annuler' : 'cancel it'
}

/** Shown in the user bubble when the cancel control is used. */
export function globalIntentCancelDisplayText(): string {
  return getSupportedLanguage() === 'fr' ? 'Annuler' : 'Cancel'
}

export function globalIntentCancelAriaLabel(): string {
  return getSupportedLanguage() === 'fr' ? 'Annuler l’action en cours' : 'Cancel current action'
}

function uiLangFr(): boolean {
  return getSupportedLanguage() === 'fr'
}

/**
 * Matches backend ``_INTENT_CANCEL_UTTERANCE`` — short cancel/stop utterances only.
 * Covers header Cancel, typed ``cancel``, and voice ``cancel it``.
 */
export function transcriptIsGlobalIntentCancel(text: string): boolean {
  const t = (text || '').trim()
  if (!t || t.length > 56) return false
  return /^(?:please\s+)?(?:cancel(?:led|ed)?|stop|abort|never\s*mind|forget(?:\s*about\s*)?it|annuler|arr[eê]t(?:er|ez)?|laissez?[\s-]*tomber|oubliez?)(?:\s+please|\s+it)?\s*[.!?…]*\s*$/i.test(
    t,
  )
}

export type GlobalCancelTaskKind =
  | 'reminder'
  | 'sports_noc'
  | 'mail'
  | 'message'
  | 'patient_search'
  | 'general_confirmation'
  | 'read_modality'
  | 'check_exam_receipt'
  | 'patient_switch'

const TASK_CANCELLED: Record<GlobalCancelTaskKind, { en: string; fr: string }> = {
  reminder: {
    en: 'The reminder has been cancelled.',
    fr: 'Le rappel est annulé.',
  },
  sports_noc: {
    en: 'The sports certificate request has been cancelled.',
    fr: 'La demande de certificat sport est annulée.',
  },
  mail: {
    en: 'The email has been cancelled.',
    fr: "L'envoi du document par e-mail est annulé.",
  },
  message: {
    en: 'The message draft has been cancelled.',
    fr: 'Le brouillon de message est annulé.',
  },
  patient_search: {
    en: 'Patient search has been cancelled.',
    fr: 'La recherche de patient est annulée.',
  },
  general_confirmation: {
    en: 'The pending confirmation has been cancelled.',
    fr: 'La confirmation en attente est annulée.',
  },
  read_modality: {
    en: 'Read-aloud has been cancelled.',
    fr: 'La lecture à voix haute est annulée.',
  },
  check_exam_receipt: {
    en: 'The exam open confirmation has been cancelled.',
    fr: "La confirmation d'ouverture de l'examen est annulée.",
  },
  patient_switch: {
    en: 'The patient switch has been cancelled.',
    fr: 'Le changement de patient est annulé.',
  },
}

const ALL_CLEAR = {
  en: "You're all clear now — you can proceed with new commands.",
  fr: 'Tout est clair — vous pouvez poursuivre avec de nouvelles commandes.',
}

/** Backend ``intent_flow_cancelled`` / generic cancel acknowledgement (EN/FR). */
const FLOW_CANCELLED = {
  en: "Okay — I've cancelled that. You can start over or ask something else.",
  fr: 'Très bien — j’ai annulé cela. Vous pouvez recommencer ou poser une autre question.',
}

const GLOBAL_CANCEL_NOTICE_TEXTS = new Set(
  [
    ALL_CLEAR.en,
    ALL_CLEAR.fr,
    FLOW_CANCELLED.en,
    FLOW_CANCELLED.fr,
    ...Object.values(TASK_CANCELLED).flatMap((row) => [row.en, row.fr]),
  ].map((s) => s.trim()),
)

/** True when assistant text is a global cancel / all-clear notice (show info hero). */
export function isGlobalCancelNoticeContent(text: string): boolean {
  const t = (text || '').trim()
  if (!t) return false
  return GLOBAL_CANCEL_NOTICE_TEXTS.has(t)
}

export function reminderCancelledAssistantText(fr?: boolean): string {
  const useFr = fr ?? uiLangFr()
  return useFr ? TASK_CANCELLED.reminder.fr : TASK_CANCELLED.reminder.en
}

/** Collect pending interactive tasks before they are cleared locally. */
export function collectPendingGlobalCancelTasks(messages: ChatMessage[]): GlobalCancelTaskKind[] {
  const kinds = new Set<GlobalCancelTaskKind>()
  if (findPendingPatientOptionsMessage(messages)) {
    kinds.add('patient_search')
  }
  for (const m of messages) {
    if (m.role !== 'assistant') continue
    if (m.awaitingRemindConfirmation) kinds.add('reminder')
    if (
      m.sportsNocNeedsClarification ||
      m.sportsNocYesNoPrompt ||
      m.sportsNocSummary ||
      m.sportsNocGenerateConfirm ||
      m.sportsNocAwaitingField
    ) {
      kinds.add('sports_noc')
    }
    if (m.awaitingMailConfirmation) kinds.add('mail')
    if (m.awaitingMessageConfirmation || m.awaitingMessageEditField) kinds.add('message')
    if (
      m.awaitingGeneralConfirmation &&
      !(
        (m.readModalityYesNoPrompt && !m.readModalityYesNoResolved) ||
        (m.generalConfirmationContext || '').trim().toLowerCase() === 'read_modality' ||
        (m.checkExamReceiptYesNoPrompt && !m.checkExamReceiptYesNoResolved) ||
        (m.generalConfirmationContext || '').trim().toLowerCase() === 'check_exam_receipt_open'
      )
    ) {
      kinds.add('general_confirmation')
    }
    if (m.readModalityYesNoPrompt && !m.readModalityYesNoResolved) kinds.add('read_modality')
    if (m.checkExamReceiptYesNoPrompt && !m.checkExamReceiptYesNoResolved) kinds.add('check_exam_receipt')
    if (m.patientSwitchConfirm && !m.patientSwitchConfirmResolved) kinds.add('patient_switch')
  }
  return [...kinds]
}

export function buildGlobalCancelAssistantMessage(
  tasks: GlobalCancelTaskKind[],
  fr?: boolean,
): string {
  const useFr = fr ?? uiLangFr()
  if (tasks.length === 0) {
    return useFr ? ALL_CLEAR.fr : ALL_CLEAR.en
  }
  return tasks
    .map((task) => (useFr ? TASK_CANCELLED[task].fr : TASK_CANCELLED[task].en))
    .join(' ')
}

function newMessageId(): string {
  return Math.random().toString(16).slice(2) + '-' + Date.now().toString(16)
}

/** Local fallback when cancel API calls fail — clears pending interactive cards in the chat. */
export function applyClientSideGlobalIntentCancel(
  messages: ChatMessage[],
  opts?: { fr?: boolean },
): ChatMessage[] {
  const fr = opts?.fr ?? uiLangFr()
  const pendingTasks = collectPendingGlobalCancelTasks(messages)
  const next = messages.map((m) => {
    if (m.role !== 'assistant') return m
    const patch: Partial<ChatMessage> = {}
    if (m.awaitingRemindConfirmation) {
      patch.awaitingRemindConfirmation = false
      patch.reminderPreviewResolved = true
    }
    if (
      m.sportsNocNeedsClarification ||
      m.sportsNocYesNoPrompt ||
      m.sportsNocSummary ||
      m.sportsNocGenerateConfirm ||
      m.sportsNocAwaitingField
    ) {
      patch.sportsNocFlowCancelled = true
      patch.sportsNocNeedsClarification = false
      patch.sportsNocAwaitingField = undefined
      patch.sportsNocGenerateConfirm = undefined
      if (m.sportsNocYesNoPrompt && !m.sportsNocYesNoResolved) {
        patch.sportsNocYesNoResolved = true
      }
    }
    if (m.awaitingMailConfirmation) {
      patch.awaitingMailConfirmation = false
    }
    if (m.awaitingMessageConfirmation || m.awaitingMessageEditField) {
      patch.awaitingMessageConfirmation = false
      patch.awaitingMessageEditField = undefined
    }
    if (m.awaitingGeneralConfirmation) {
      patch.awaitingGeneralConfirmation = false
    }
    if (m.readModalityYesNoPrompt && !m.readModalityYesNoResolved) {
      patch.readModalityYesNoResolved = true
      patch.awaitingGeneralConfirmation = false
    }
    if (m.checkExamReceiptYesNoPrompt && !m.checkExamReceiptYesNoResolved) {
      patch.checkExamReceiptYesNoResolved = true
      patch.awaitingGeneralConfirmation = false
    }
    if (m.patientSwitchConfirm && !m.patientSwitchConfirmResolved) {
      patch.patientSwitchConfirmResolved = true
    }
    if (Object.keys(patch).length === 0) return m
    return { ...m, ...patch }
  })
  const assistantMsg: ChatMessage = {
    id: newMessageId(),
    role: 'assistant',
    content: buildGlobalCancelAssistantMessage(pendingTasks, fr),
    timestamp: Date.now(),
    cancelNotice: true,
    sportsNocFlowCancelled: pendingTasks.includes('sports_noc') ? true : undefined,
  }
  return [...next, assistantMsg]
}
